import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

/**
 * Theme tokens tuned to feel native to the Figma desktop UI — ported from the
 * pdf.to.design plugin so every importer shares one look.
 *
 *  - A controlled three-tier text contrast (white / 70% / 50%) so hierarchy
 *    reads at a glance.
 *  - Translucent borders (rgba whites) that layer correctly on every elevation.
 *  - Multi-stop, low-alpha shadows that feel like Figma's own popovers.
 */
const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        figma: {
          bg: { value: '#2C2C2C' },
          bgSubtle: { value: '#262626' },
          bgDark: { value: '#1E1E1E' },
          panel: { value: '#383838' },
          panelHover: { value: '#404040' },
          elevated: { value: '#3F3F3F' },

          border: { value: 'rgba(255, 255, 255, 0.08)' },
          borderStrong: { value: 'rgba(255, 255, 255, 0.14)' },
          borderLight: { value: 'rgba(255, 255, 255, 0.14)' },

          text: { value: '#FFFFFF' },
          textSecondary: { value: '#B3B3B3' },
          textMuted: { value: '#7F7F7F' },

          accent: { value: '#0D99FF' },
          accentHover: { value: '#0A84DB' },
          accentMuted: { value: 'rgba(13, 153, 255, 0.16)' },
          accentSubtle: { value: 'rgba(13, 153, 255, 0.08)' },

          success: { value: '#14AE5C' },
          successMuted: { value: 'rgba(20, 174, 92, 0.14)' },
          warning: { value: '#FFCD29' },
          warningMuted: { value: 'rgba(255, 205, 41, 0.14)' },
          error: { value: '#F24822' },
          errorMuted: { value: 'rgba(242, 72, 34, 0.14)' },

          primary: { value: '#0D99FF' },
          primaryHover: { value: '#0A84DB' },
          primaryMuted: { value: 'rgba(13, 153, 255, 0.16)' },
          brand: { value: '#FFFFFF' },
        },
      },
      fonts: {
        heading: {
          value: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        body: {
          value: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        mono: {
          value: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, monospace',
        },
      },
      fontSizes: {
        '2xs': { value: '0.6875rem' },
        xs: { value: '0.75rem' },
        sm: { value: '0.8125rem' },
        md: { value: '0.875rem' },
        lg: { value: '1rem' },
        xl: { value: '1.125rem' },
        '2xl': { value: '1.375rem' },
        '3xl': { value: '1.75rem' },
      },
      fontWeights: {
        normal: { value: '400' },
        medium: { value: '500' },
        semibold: { value: '600' },
        bold: { value: '700' },
      },
      letterSpacings: {
        tight: { value: '-0.011em' },
        snug: { value: '-0.005em' },
        normal: { value: '0' },
        wide: { value: '0.04em' },
      },
      lineHeights: {
        tight: { value: '1.2' },
        snug: { value: '1.35' },
        normal: { value: '1.5' },
        relaxed: { value: '1.6' },
      },
      radii: {
        none: { value: '0' },
        sm: { value: '4px' },
        md: { value: '6px' },
        lg: { value: '8px' },
        xl: { value: '10px' },
        '2xl': { value: '14px' },
        full: { value: '9999px' },
      },
      spacing: {
        px: { value: '1px' },
        0.5: { value: '2px' },
        1: { value: '4px' },
        1.5: { value: '6px' },
        2: { value: '8px' },
        2.5: { value: '10px' },
        3: { value: '12px' },
        4: { value: '16px' },
        5: { value: '20px' },
        6: { value: '24px' },
        8: { value: '32px' },
        10: { value: '40px' },
        12: { value: '48px' },
        14: { value: '56px' },
        16: { value: '64px' },
      },
      shadows: {
        sm: { value: '0 1px 2px rgba(0, 0, 0, 0.18)' },
        md: { value: '0 2px 6px rgba(0, 0, 0, 0.20), 0 1px 2px rgba(0, 0, 0, 0.14)' },
        lg: { value: '0 6px 16px rgba(0, 0, 0, 0.28), 0 2px 6px rgba(0, 0, 0, 0.16)' },
        xl: { value: '0 16px 36px rgba(0, 0, 0, 0.36), 0 4px 10px rgba(0, 0, 0, 0.20)' },
        focusRing: {
          value: '0 0 0 1px rgba(13, 153, 255, 0.7), 0 0 0 3px rgba(13, 153, 255, 0.18)',
        },
      },
    },
    semanticTokens: {
      colors: {
        'bg.canvas': { value: '{colors.figma.bg}' },
        'bg.default': { value: '{colors.figma.panel}' },
        'bg.subtle': { value: '{colors.figma.panelHover}' },
        'bg.elevated': { value: '{colors.figma.elevated}' },
        'bg.header': { value: '{colors.figma.bgDark}' },
        'fg.default': { value: '{colors.figma.text}' },
        'fg.muted': { value: '{colors.figma.textSecondary}' },
        'fg.subtle': { value: '{colors.figma.textMuted}' },
        'border.default': { value: '{colors.figma.border}' },
        'border.strong': { value: '{colors.figma.borderStrong}' },
        'accent.default': { value: '{colors.figma.accent}' },
        'accent.hover': { value: '{colors.figma.accentHover}' },
        'accent.muted': { value: '{colors.figma.accentMuted}' },
        'success.default': { value: '{colors.figma.success}' },
        'warning.default': { value: '{colors.figma.warning}' },
        'error.default': { value: '{colors.figma.error}' },
      },
    },
  },
  globalCss: {
    'html, body, #root': {
      bg: 'bg.canvas',
      color: 'fg.default',
      fontFamily: 'body',
      fontSize: 'sm',
      lineHeight: 'normal',
      fontFeatureSettings: '"cv11", "ss01", "kern"',
    },
    'html, body': {
      margin: 0,
      height: '100%',
    },
    '#root': {
      height: '100%',
    },
    '::selection': {
      bg: 'accent.muted',
      color: 'fg.default',
    },
  },
});

export const system = createSystem(defaultConfig, config);
